import { Check, X } from "lucide-react";
import React from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import { checkPassword } from "../../utils/passwordPolicy";

interface PasswordRequirementsProps {
  password: string;
  confirmPassword?: string;
}

/**
 * Regras de senha para criar/alterar (mesma política do backend e do CRM — ver
 * utils/passwordPolicy.ts): 8+ caracteres, maiúscula e minúscula, número e caractere especial.
 */
export const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({
  password,
  confirmPassword = "",
}) => {
  const { t } = useTranslation();

  if (!password && !confirmPassword) return null;

  const checks = checkPassword(password);
  const rules = [
    { ok: checks.minLength, text: t("Mínimo de 8 caracteres") },
    {
      ok: checks.upper && checks.lower,
      text: t("Letras maiúsculas e minúsculas"),
    },
    { ok: checks.number, text: t("Ao menos um número") },
    {
      ok: checks.special,
      text: t("Ao menos um caractere especial (!@#$...)"),
    },
  ];
  const showMatch = confirmPassword.length > 0;
  const matches = password === confirmPassword;

  const Item = ({
    ok,
    text,
    negative,
  }: {
    ok: boolean;
    text: string;
    negative?: boolean;
  }) => (
    <li
      className={`flex items-center gap-2 transition-colors ${
        ok
          ? "font-medium text-emerald-600"
          : negative
            ? "text-rose-600"
            : "text-slate-500"
      }`}
    >
      <span
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${
          ok
            ? "bg-emerald-100 text-emerald-700"
            : negative
              ? "bg-rose-100 text-rose-700"
              : "bg-slate-200 text-slate-500"
        }`}
      >
        {ok ? (
          <Check className="h-3 w-3 stroke-3" />
        ) : negative ? (
          <X className="h-3 w-3 stroke-3" />
        ) : (
          <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
        )}
      </span>
      <span>{text}</span>
    </li>
  );

  return (
    <div className="space-y-1.5 rounded-lg border border-slate-100 bg-slate-50/70 p-3 text-xs">
      <p className="font-medium text-slate-700">{t("Requisitos da senha:")}</p>
      <ul className="space-y-1">
        {rules.map((rule) => (
          <Item key={rule.text} {...rule} />
        ))}
        {showMatch && (
          <Item
            ok={matches}
            negative={!matches}
            text={
              matches
                ? t("Senhas conferem")
                : t("As senhas devem ser idênticas")
            }
          />
        )}
      </ul>
    </div>
  );
};
